import {
  Button,
  FormControl,
  FormLabel,
  VStack,
  useToast,
  Box,
  Textarea,
  Text,
  Heading,
} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import {MEDICAL_FIELDS, updatePatient} from "./helpers/updatePatient.js";
import UnlimitedItemFields from "./Fields/UnlimitedItemField.jsx";
import {pharmAutocomplete} from "../../crudRequests";
import {SearchableSelect} from "./Fields/SearchableSelect.jsx";

export default function MedicalHistory({state, dispatch}) {
  const [patient, setPatient] = useState(state.patient);
  const [isLoading, setIsLoading] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const toast = useToast();

  useEffect(() => {
    const getPharmacies = async () => {
      try {
        const res = await pharmAutocomplete(state, "");
        setPharmacies(res?.data ?? []);
      } catch (err) {
        setPharmacies([]);
        console.error("Failed to load pharmacies:", err);
      }
    };
    getPharmacies();
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
      const results = await pharmAutocomplete(state, inputValue);
      return (
        results?.data?.map((pharmacy) => ({
          ...pharmacy,
          value: pharmacy.phid,
          label: `${pharmacy.name} ${pharmacy.address} ${pharmacy.phone} ${pharmacy.fax}`,
        })) || []
      );
    } catch (err) {
      console.error("Pharmacy search failed:", err);
      return [];
    }
  };

  return (
    <Box w="full" px={0} py={2}>
      <form
        onSubmit={async (e) => {
          try {
            setIsLoading(true);
            e.preventDefault();
            await updatePatient({patientData: patient, fields: MEDICAL_FIELDS, state});
            dispatch({type: "UPDATE_PATIENT", payload: patient});
            toast({
              title: "Saved",
              description: "Medical history updated successfully",
              status: "success",
              duration: 4000,
              isClosable: true,
            });
          } catch (error) {
            toast({
              title: "Information Not Updated",
              description: error?.response?.data || error.message,
              status: "error",
              isClosable: true,
            });
          } finally {
            setIsLoading(false);
          }
        }}
      >
        <VStack spacing={4} align="stretch">
          <Box
            as="div"
            display="grid"
            gridTemplateColumns={{base: "1fr", md: "repeat(2, minmax(0, 1fr))"}}
            gap={3}
          >
            <Box
              p={{base: 4, md: 5}}
              bg="gray.50"
              borderRadius="12px"
            >
              <UnlimitedItemFields
                label="Medications"
                items={patient.medications}
                setItems={(medications) =>
                  setPatient({...patient, medications})
                }
                fields={[
                  {name: "name", placeholder: "Name", type: "medicationSelect"},
                ]}
                dashState={state}
              />
            </Box>
            <Box
              p={{base: 4, md: 5}}
              bg="gray.50"
              borderRadius="12px"
            >
              <UnlimitedItemFields
                label="Allergies"
                items={patient.allergies}
                setItems={(allergies) => setPatient({...patient, allergies})}
                fields={[{name: "name", placeholder: "Name", type: "text"}]}
              />
            </Box>
          </Box>

          <Box
            as="div"
            display="grid"
            gridTemplateColumns={{base: "1fr", md: "repeat(2, minmax(0, 1fr))"}}
            gap={3}
          >
            <Box
              p={{base: 4, md: 5}}
              bg="gray.50"
              borderRadius="12px"
            >
              <UnlimitedItemFields
                label="Pre-diagnosed Medical Diseases"
                items={patient.preDiagnosedMedicalConditions}
                setItems={(preDiagnosedMedicalConditions) =>
                  setPatient({...patient, preDiagnosedMedicalConditions})
                }
                fields={[{name: "name", placeholder: "Name", type: "text"}]}
              />
            </Box>
            <Box
              p={{base: 4, md: 5}}
              bg="gray.50"
              borderRadius="12px"
            >
              <UnlimitedItemFields
                label="Pre-diagnosed Mental Health Disorders"
                items={patient.preDiagnosedMentalHealthDisorders}
                setItems={(preDiagnosedMentalHealthDisorders) =>
                  setPatient({...patient, preDiagnosedMentalHealthDisorders})
                }
                fields={[{name: "name", placeholder: "Name", type: "text"}]}
              />
            </Box>
          </Box>

          <Box
            as="div"
            display="grid"
            gridTemplateColumns={{base: "1fr", md: "repeat(2, minmax(0, 1fr))"}}
            gap={3}
          >
            <Box
              p={{base: 4, md: 5}}
              bg="gray.50"
              borderRadius="12px"
            >
              <FormControl my={0}>
                <FormLabel fontSize="md" color="gray.700">
                  Family Health/Mental Health Problems
                </FormLabel>
                <Textarea
                  size="sm"
                  variant="outline"
                  borderRadius="md"
                  placeholder="Father has had diabetes for 20 years. Aunt suffers from depression since childhood."
                  value={patient.familyHealthProblems || ""}
                  onChange={(e) =>
                    setPatient({
                      ...patient,
                      familyHealthProblems: e.target.value,
                    })
                  }
                />
              </FormControl>
            </Box>
            <Box
              p={{base: 4, md: 5}}
              bg="gray.50"
              borderRadius="12px"
            >
              <SearchableSelect
                fontSize="md"
                mb="2"
                async={true}
                isRequired={false}
                label="Preferred Pharmacy"
                placeholder="Search for a pharmacy"
                value={patient.preferredPharmacy || null}
                onChange={(option) =>
                  setPatient({
                    ...patient,
                    preferredPharmacy: option ? option.value : null,
                  })
                }
                loadOptions={loadOptions}
                defaultOptions={[
                  {label: "None", value: null},
                  ...pharmacies.map((option) => ({
                    ...option,
                    label: `${option.name} ${option.address} ${option.phone} ${option.fax}`,
                    value: option.phid,
                  })),
                ]}
                color="black"
                fontWeight="medium"
                size="sm"
                formatOptionLabel={(option) =>
                  option.value === null ? (
                    <Box>
                      <Text fontSize="sm">None</Text>
                    </Box>
                  ) : (
                    <Box>
                      <Text fontSize="sm">{option.name}</Text>
                      <Text fontSize="10px">{option.address}</Text>
                      <Text fontSize="10px">Phone: {option.phone}</Text>
                      <Text fontSize="10px">Fax: {option.fax}</Text>
                    </Box>
                  )
                }
              />
            </Box>
          </Box>

          <Button
            isLoading={isLoading}
            variant="outline"
            borderColor="gray.300"
            bg="white"
            _hover={{bg: "#f8f9fa"}}
            type="submit"
            my={3}
            px="8"
            size="sm"
            alignSelf="center"
          >
            Save
          </Button>
        </VStack>
      </form>
    </Box>
  );
}
